import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { apiResource, b2chatBaseQuery } from '@src/client';
import {
  AssistantApiRecord,
  AssistantMessageDefaults,
  AssistantMessageDefaultsResponse,
  AssistantFollowUpConfig,
  AssistantForm,
  AssistantStatus,
  AssistantTable,
  AssistantTableFilters,
  CreateMcpConnectionRequest,
  CreateMcpConnectionResponse,
  DiscoverMcpConnectionResponse,
  fetchAssistantApi,
  GetMcpConnectionResponse,
  GetMcpOperationResponse,
  MCPConnection,
  MCPConnectionStatus,
  McpOAuthAuthorizeRequest,
  McpOAuthAuthorizeResponse,
  McpOAuthTokenRequest,
  McpOAuthTokenResponse,
  UpdateMcpConnectionDetailsRequest,
  UpdateMcpConnectionDetailsResponse,
  UpdateMcpConnectionToolsRequest,
  UpdateMcpConnectionToolsResponse,
} from '@src/types/assistant';
import {
  assistantApiErrorResult,
  fromAssistantApi,
  handleMcpConnectionError,
  messageDefaultsFromApi,
  toAssistantApiBody,
} from '@src/utils/assistant';
import env from '@src/utils/env';
import { sortAssistants } from '@src/utils/sorts';

export type SearchAssistantsResponse = {
  paging: {
    hasMore: boolean;
    offset: number;
    limit: number;
  };
  data: AssistantTable[];
};

const AssistantApi = createApi({
  reducerPath: 'assistantApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: [
    'assistant-list',
    'assistant',
    'assistant-followup-config',
    'assistant-message-defaults',
  ],
  endpoints: builder => ({
    getMcpConnections: builder.query<MCPConnection[], { assistantId: string }>({
      query: ({ assistantId }) =>
        apiResource(async () => {
          const response = await fetchAssistantApi<MCPConnection[]>(
            `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections`
          );
          return { data: response, error: null };
        }, {}),
      providesTags: ['assistant-list'],
    }),
    searchAssistants: builder.query<
      SearchAssistantsResponse,
      AssistantTableFilters & { limit?: number; offset?: number }
    >({
      query: request =>
        apiResource(
          async options => {
            const response = await fetchAssistantApi(
              `${
                env.WEB_SERVICE_ASSISTANTS_BASE_URI
              }/assistants?${new URLSearchParams(
                options?.params as Record<string, string>
              ).toString()}`
            );

            return { data: response as SearchAssistantsResponse, error: null };
          },
          {
            params: {
              limit: `${request.limit}`,
              offset: `${request.offset}`,
              onlyActive: `${request.onlyActive}`,
              nameLike: `${request.nameLike}`,
            },
          }
        ),
      serializeQueryArgs: ({ endpointName }) => ({ endpointName }),
      merge: (
        currentCacheData: SearchAssistantsResponse,
        responseData: SearchAssistantsResponse
      ) => {
        if (!currentCacheData) {
          return responseData;
        }
        const data = currentCacheData.data.slice();
        data.splice(responseData.paging.offset, Infinity, ...responseData.data);
        return {
          ...currentCacheData,
          ...responseData,
          data,
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      transformResponse(
        baseQueryReturnValue: SearchAssistantsResponse,
        _,
        arg
      ) {
        baseQueryReturnValue.paging.limit = Number(arg.limit);
        baseQueryReturnValue.paging.offset = Number(arg.offset);
        return baseQueryReturnValue;
      },
      extraOptions: {
        maxRetries: 1,
      },
      providesTags: ['assistant-list'],
    }),

    updateAssistantStatus: builder.mutation<
      void,
      { assistantId: string; status: string }
    >({
      query: ({ assistantId, status }) =>
        apiResource(async () => {
          await fetchAssistantApi(
            `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}`,
            {
              method: 'PATCH',
              body: JSON.stringify({ status }),
            }
          );
          return { data: {}, error: null };
        }, {}),
      onQueryStarted: async (
        { assistantId, status },
        { dispatch, queryFulfilled, getState }
      ) => {
        await queryFulfilled;

        dispatch(
          AssistantApi.util.updateQueryData('searchAssistants', {}, draft => {
            const assistantIndex = draft.data.findIndex(
              ({ id }) => id === assistantId
            );
            if (assistantIndex !== -1)
              draft.data[assistantIndex].status = status;

            const apiState = getState();
            const assistantApiState = apiState.assistantApi;
            const queries = assistantApiState?.queries || {};

            const queryArgs = Object.values(queries).find(
              q => q?.endpointName === 'searchAssistants'
            )?.originalArgs as AssistantTableFilters;

            if (queryArgs?.onlyActive && status !== AssistantStatus.ACTIVE) {
              draft.data = draft.data.filter(a => a.id !== assistantId);
            }

            draft.data = sortAssistants(draft.data);
          })
        );
      },
    }),
    activateMcpConnection: builder.mutation<
      { status: MCPConnectionStatus.ACTIVE },
      { assistantId: string; connectionId: string }
    >({
      query: ({ assistantId, connectionId }) =>
        apiResource(async () => {
          try {
            const response = await fetchAssistantApi<{ status: string }>(
              `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections/${connectionId}/activate`,
              { method: 'POST' }
            );
            return { data: response, error: null };
          } catch (err) {
            return handleMcpConnectionError(err);
          }
        }, {}),
      invalidatesTags: ['assistant-list'],
    }),

    deactivateMcpConnection: builder.mutation<
      { status: MCPConnectionStatus.DISABLED },
      { assistantId: string; connectionId: string }
    >({
      query: ({ assistantId, connectionId }) =>
        apiResource(async () => {
          const response = await fetchAssistantApi<{ status: string }>(
            `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections/${connectionId}/deactivate`,
            { method: 'POST' }
          );
          return { data: response, error: null };
        }, {}),
      invalidatesTags: ['assistant-list'],
    }),

    deleteMcpConnection: builder.mutation<
      void,
      { assistantId: string; connectionId: string }
    >({
      query: ({ assistantId, connectionId }) =>
        apiResource(async () => {
          await fetchAssistantApi(
            `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections/${connectionId}`,
            { method: 'DELETE' }
          );
          return { data: {}, error: null };
        }, {}),
      invalidatesTags: ['assistant-list'],
    }),

    createMcpConnection: builder.mutation<
      CreateMcpConnectionResponse,
      { assistantId: string; body: CreateMcpConnectionRequest }
    >({
      query: ({ assistantId, body }) =>
        apiResource(async () => {
          try {
            const response =
              await fetchAssistantApi<CreateMcpConnectionResponse>(
                `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections`,
                { method: 'POST', body: JSON.stringify(body) }
              );
            return { data: response, error: null };
          } catch (err) {
            return handleMcpConnectionError(err);
          }
        }, {}),
      invalidatesTags: ['assistant-list'],
    }),

    discoverMcpConnection: builder.mutation<
      DiscoverMcpConnectionResponse,
      { assistantId: string; connectionId: string }
    >({
      query: ({ assistantId, connectionId }) =>
        apiResource(async () => {
          const response =
            await fetchAssistantApi<DiscoverMcpConnectionResponse>(
              `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections/${connectionId}/discover`,
              { method: 'POST' }
            );
          return { data: response, error: null };
        }, {}),
      invalidatesTags: ['assistant-list'],
    }),

    getMcpConnection: builder.query<
      GetMcpConnectionResponse,
      { assistantId: string; connectionId: string }
    >({
      query: ({ assistantId, connectionId }) =>
        apiResource(async () => {
          const response = await fetchAssistantApi<GetMcpConnectionResponse>(
            `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections/${connectionId}`
          );
          return { data: response, error: null };
        }, {}),
      providesTags: ['assistant-list'],
    }),

    getMcpOperation: builder.query<
      GetMcpOperationResponse,
      { operationId: string }
    >({
      query: ({ operationId }) =>
        apiResource(async () => {
          const response = await fetchAssistantApi<GetMcpOperationResponse>(
            `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/mcp-operations/${operationId}`
          );
          return { data: response, error: null };
        }, {}),
    }),

    updateMcpConnectionTools: builder.mutation<
      UpdateMcpConnectionToolsResponse,
      {
        assistantId: string;
        connectionId: string;
        body: UpdateMcpConnectionToolsRequest;
      }
    >({
      query: ({ assistantId, connectionId, body }) =>
        apiResource(async () => {
          try {
            const response =
              await fetchAssistantApi<UpdateMcpConnectionToolsResponse>(
                `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections/${connectionId}/tools`,
                { method: 'PUT', body: JSON.stringify(body) }
              );
            return { data: response, error: null };
          } catch (err) {
            return handleMcpConnectionError(err);
          }
        }, {}),
      invalidatesTags: ['assistant-list'],
    }),

    updateMcpConnectionDetails: builder.mutation<
      UpdateMcpConnectionDetailsResponse,
      {
        assistantId: string;
        connectionId: string;
        body: UpdateMcpConnectionDetailsRequest;
      }
    >({
      query: ({ assistantId, connectionId, body }) =>
        apiResource(async () => {
          try {
            const response =
              await fetchAssistantApi<UpdateMcpConnectionDetailsResponse>(
                `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/mcp-connections/${connectionId}`,
                { method: 'PUT', body: JSON.stringify(body) }
              );
            return { data: response, error: null };
          } catch (err) {
            return handleMcpConnectionError(err);
          }
        }, {}),
      invalidatesTags: ['assistant-list'],
    }),

    getAssistant: builder.query<AssistantForm, { assistantId: string }>({
      query: ({ assistantId }) =>
        apiResource(async () => {
          const assistant = await fetchAssistantApi<AssistantApiRecord>(
            `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}`,
            { method: 'GET' }
          );
          return { data: assistant, error: null };
        }, {}),
      transformResponse: fromAssistantApi,
      providesTags: ['assistant'],
    }),

    // F001 system default replies: static and global, one request per session.
    getAssistantMessageDefaults: builder.query<AssistantMessageDefaults, void>({
      query: () =>
        apiResource(async () => {
          const defaults =
            await fetchAssistantApi<AssistantMessageDefaultsResponse>(
              `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/message-defaults`,
              { method: 'GET' }
            );
          return { data: defaults, error: null };
        }, {}),
      transformResponse: messageDefaultsFromApi,
      keepUnusedDataFor: 60 * 60,
      providesTags: ['assistant-message-defaults'],
    }),

    createAssistant: builder.mutation<
      { id: string; assistantStoreId: string },
      { assistant: AssistantForm }
    >({
      query: ({ assistant }) =>
        apiResource(async () => {
          try {
            const newAssistant = await fetchAssistantApi<{ id: string }>(
              `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants`,
              {
                method: 'POST',
                body: JSON.stringify(toAssistantApiBody(assistant)),
              }
            );

            const assistantStore = await fetchAssistantApi<{ id: string }>(
              `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/vector-stores/${newAssistant.id}`,
              { method: 'POST' }
            );

            return {
              data: {
                id: newAssistant.id,
                assistantStoreId: assistantStore.id,
              },
              error: null,
            };
          } catch (err) {
            return assistantApiErrorResult(err);
          }
        }, {}),
      invalidatesTags: ['assistant', 'assistant-list'],
    }),

    updateAssistant: builder.mutation<
      { id: string },
      {
        assistantId: string;
        assistant: AssistantForm;
        assistantStatusIsChanged?: boolean;
      }
    >({
      query: ({ assistantId, assistant, assistantStatusIsChanged = false }) =>
        apiResource(async () => {
          const baseUrl = `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}`;

          try {
            const assistantUpdated = await fetchAssistantApi<{ id: string }>(
              baseUrl,
              {
                method: 'PUT',
                body: JSON.stringify({
                  ...toAssistantApiBody(assistant),
                  status: assistantStatusIsChanged
                    ? AssistantStatus.INACTIVE
                    : assistant.status,
                }),
              }
            );

            if (assistantStatusIsChanged) {
              await fetchAssistantApi(baseUrl, {
                method: 'PATCH',
                body: JSON.stringify({ status: assistant.status }),
              });
            }

            return { data: { id: assistantUpdated.id }, error: null };
          } catch (err) {
            return assistantApiErrorResult(err);
          }
        }, {}),
      invalidatesTags: ['assistant', 'assistant-list'],
    }),
    getAssistantFollowupsConfig: builder.query<
      AssistantFollowUpConfig,
      { assistantId: string }
    >({
      queryFn: async ({ assistantId }) => {
        try {
          const baseUrl = `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/followups/config`;
          const data = await fetchAssistantApi<AssistantFollowUpConfig>(
            baseUrl
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ['assistant-followup-config'],
    }),

    updateAssistantFollowupsConfig: builder.mutation<
      AssistantFollowUpConfig,
      { assistantId: string; config: AssistantFollowUpConfig }
    >({
      queryFn: async ({ assistantId, config }) => {
        try {
          const baseUrl = `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/followups/config`;
          const data = await fetchAssistantApi<AssistantFollowUpConfig>(
            baseUrl,
            {
              method: 'PUT',
              body: JSON.stringify(config),
            }
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ['assistant-followup-config'],
    }),
    mcpOAuthAuthorize: builder.mutation<
      McpOAuthAuthorizeResponse,
      { body: McpOAuthAuthorizeRequest }
    >({
      query: ({ body }) =>
        apiResource(async () => {
          try {
            const response = await fetchAssistantApi<McpOAuthAuthorizeResponse>(
              `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/mcp-oauth/authorize`,
              { method: 'POST', body: JSON.stringify(body) }
            );
            return { data: response, error: null };
          } catch (err) {
            return handleMcpConnectionError(err);
          }
        }, {}),
    }),

    mcpOAuthToken: builder.mutation<
      McpOAuthTokenResponse,
      { authorizeId: string; body: McpOAuthTokenRequest }
    >({
      query: ({ authorizeId, body }) =>
        apiResource(async () => {
          try {
            const response = await fetchAssistantApi<McpOAuthTokenResponse>(
              `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/mcp-oauth/${authorizeId}/token`,
              { method: 'POST', body: JSON.stringify(body) }
            );
            return { data: response, error: null };
          } catch (err) {
            return handleMcpConnectionError(err);
          }
        }, {}),
    }),
  }),
});

export const {
  useSearchAssistantsQuery,
  useLazySearchAssistantsQuery,
  useUpdateAssistantStatusMutation,
  useGetMcpConnectionsQuery,
  useLazyGetMcpConnectionsQuery,
  useActivateMcpConnectionMutation,
  useDeactivateMcpConnectionMutation,
  useDeleteMcpConnectionMutation,
  useCreateMcpConnectionMutation,
  useDiscoverMcpConnectionMutation,
  useGetMcpOperationQuery,
  useLazyGetMcpOperationQuery,
  useGetMcpConnectionQuery,
  useLazyGetMcpConnectionQuery,
  useUpdateMcpConnectionToolsMutation,
  useUpdateMcpConnectionDetailsMutation,
  useGetAssistantQuery,
  useGetAssistantMessageDefaultsQuery,
  useCreateAssistantMutation,
  useUpdateAssistantMutation,
  useGetAssistantFollowupsConfigQuery,
  useUpdateAssistantFollowupsConfigMutation,
  useMcpOAuthAuthorizeMutation,
  useMcpOAuthTokenMutation,
} = AssistantApi;

export default AssistantApi;
